
import React, { useState, useEffect, useCallback } from 'react'
import MaterialTypography from '../../../../../../../components/MaterialTypography'
import MaterialBox from '../../../../../../../components/MaterialBox'
import { LuAlarmClock, LuPersonStanding } from 'react-icons/lu';
import './index.css'
import { Box, Grid } from '@mui/material';
import { fetchData, patchData } from '../../../../../../Utils/Baseurl';
import { useLoading } from '../../../../../../../context/loadingcontext';
import MaterialSelect from '../../../../../../../components/MaterialSelect';
import { getTestCaseStatusMiddleware } from '../../../../../../../api/testCaseStatusMiddleware';
import { useSnackbar } from '../../../../../../../components/SnackbarProvider';

const StatusProgress = ({ statusData }) => {

    const statuses = [
        { key: 'passed', label: 'Passed', color: '#16a34a' },
        { key: 'failed', label: 'Failed', color: '#dc2626' },
        { key: 'blocked', label: 'Blocked', color: '#f97316' },
        { key: 'retest', label: 'Retest', color: '#facc15' },
        { key: 'skipped', label: 'Skipped', color: '#3b82f6' },
        { key: 'untested', label: 'Untested', color: '#9ca3af' }
    ];

    const total = Object.values(statusData).reduce((sum, val) => sum + val, 0);
    const normalized = total ? total : 1;

    return (
        <div className="status-progress-container">
            <div className="progress-bar">
                {statuses.map((s) => {
                    const width = (statusData[s.key] / normalized) * 100;
                    return (
                        <div
                            key={s.key}
                            className="progress-segment"
                            style={{
                                width: `${width}%`,
                                backgroundColor: s.color,
                            }}
                        ></div>
                    );
                })}
            </div>

            <div className="status-row">
                {statuses.map((s) => (
                    <div key={s.key} className="status-item">
                        <span
                            className="status-dot"
                            style={{ backgroundColor: s.color }}
                        ></span>
                        <span className="status-label">
                            {s.label} ({statusData[s.key] || 0})
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TestCaseStatus = ({ runId }) => {
    console.log("TestCaseStatus component rendered, runId:", runId);
    const { setCirleLoading } = useLoading();

    const [runStatus, setRunStatus] = useState({
        passed: 0,
        failed: 0,
        blocked: 0,
        retest: 0,
        skipped: 0,
        untested: 0,
    });

    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusOptions, setStatusOptions] = useState([]);
    const [assignedData, setAssignedData] = useState('');
    const [runTime, setRunTime] = useState('');
    const { showSnackbar } = useSnackbar();
    const rowsPerPage = 10;

    useEffect(() => {
        const fetchStatusOptions = async () => {
            try {
                const response = await getTestCaseStatusMiddleware();
                console.log("Status options response:", response.status);
                if (response && response.status) {
                    const formattedOptions = response.status?.map((item) => ({
                        value: item.value,
                        label: item.label
                    }));
                    setStatusOptions(formattedOptions);
                }
            } catch (error) {
                console.error("Error fetching status options:", error);
            }
        };
        fetchStatusOptions();
    }, []);

    const fetchTestCases = useCallback(async () => {
        console.log("Fetching test cases for runId:", runId);
        setCirleLoading(true);
        try {
            const response = await fetchData(`api/testrun_testcase/${runId}/`);
            console.log("API Response:", response);
            if (response) {

                const formattedData = (response.data || []).map((item) => ({
                    id: item.id,
                    case_id: item.case_id,
                    title: item.title,
                    priority: item.priority,
                    type: item.test_case_type,
                    owner: item.owner_name,
                    assigned: item.assigned_name || '-',
                    status: item.test_status || 'Untested',
                    module: item.module_name,
                    project: item.project_name,
                }));
                setData(formattedData);


                if (response.test_cases) {
                    setRunStatus({
                        passed: response.test_cases.passed || 0,
                        failed: response.test_cases.failed || 0,
                        blocked: response.test_cases.blocked || 0,
                        retest: response.test_cases.retest || 0,
                        skipped: response.test_cases.skipped || 0,
                        untested: response.test_cases.untested || 0,
                    });
                    setAssignedData(response.test_cases?.ran_by)
                    setRunTime(response.test_cases?.ran_time)
                }
            }
        } catch (error) {
            console.error("Error fetching test run test cases:", error);
            setData([]);
        } finally {
            setCirleLoading(false);
        }
    }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        console.log("TestCaseStatus useEffect triggered, runId:", runId);

        if (!runId) {
            console.log("runId is null/undefined, skipping fetch");
            return;
        }

        fetchTestCases();
    }, [runId, fetchTestCases]);

    const totalPages = Math.ceil(data.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const currentRows = data.slice(startIndex, startIndex + rowsPerPage);

    const handleStatusChange = async (id, selectedOption) => {
        const newStatusLabel = selectedOption?.label || '';
        const newStatusValue = selectedOption?.value || '';

        const updated = data.map((row) =>
            row.id === id ? { ...row, status: newStatusLabel } : row
        );
        setData(updated);

        try {
            setCirleLoading(true);
            const payload = { test_status: newStatusValue };
            console.log("Payload:", payload);
            await patchData(`api/testcase/${id}/`, payload);
            showSnackbar('Status updated successfully!', 'success');
            fetchTestCases();
        } catch (error) {
            console.error("Error updating test case status:", error);
            showSnackbar('Failed to update status', 'error');
        } finally {
            setCirleLoading(false);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePageClick = (page) => {
        setCurrentPage(page);
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                <MaterialTypography variant='h6' fontWeight='bold' color='dark'>Test Case Name</MaterialTypography>
                <MaterialBox sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                    <MaterialBox sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignContent: 'center' }}>
                        <LuPersonStanding />
                        <MaterialTypography variant='caption' color='secondary'>{assignedData}</MaterialTypography>
                    </MaterialBox>
                    <MaterialBox sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignContent: 'center' }}>
                        <LuAlarmClock />
                        <MaterialTypography variant='caption' color='secondary'>{runTime}</MaterialTypography>
                    </MaterialBox>
                </MaterialBox>
            </Box>
            <StatusProgress statusData={runStatus} />
            <Box sx={{ padding: 1 }}>
                <Grid container spacing={1} direction='column' mt={1}>
                    <Grid size={{ xs: 12, sm: 12 }}>
                        <div className="table-container">
                            <table className="custom-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Title</th>
                                        <th>Priority</th>
                                        <th>Type of Test Case</th>
                                        <th>Owner</th>
                                        <th>Assigned</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.case_id}</td>
                                            <td>{row.title}</td>
                                            <td>{row.priority}</td>
                                            <td>{row.type}</td>
                                            <td>{row.owner}</td>
                                            <td>{row.assigned}</td>
                                            <td>
                                                <MaterialSelect
                                                    size="small"
                                                    value={statusOptions.find(opt => opt.label === row.status) || { value: row.status, label: row.status }}
                                                    options={statusOptions}
                                                    onChange={(val) => handleStatusChange(row.id, val)}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    sx={{ minWidth: 120 }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="pagination">
                            <button onClick={handlePrev} disabled={currentPage === 1}>
                                Previous
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => handlePageClick(i + 1)}
                                    className={currentPage === i + 1 ? "active" : ""}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button onClick={handleNext} disabled={currentPage === totalPages}>
                                Next
                            </button>
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export default TestCaseStatus
